<template>
  <div v-if="runDetail" class="space-y-4">
    <p v-if="runDetail.error_message" class="text-xs text-red-300 rounded-md border border-red-400/30 bg-red-950/30 px-2 py-1.5">
      <strong>Erro:</strong> {{ runDetail.error_message }}
    </p>

    <div class="mb-3 rounded-lg border border-slate-600/60 bg-[#2f4563]/50 p-3 space-y-2 text-xs text-slate-300">
      <p class="text-[11px] uppercase tracking-wide text-cyan-200/90 font-semibold">Entrada</p>
      <div v-if="inputUserMessage()" class="text-slate-200">
        <p class="text-slate-400 mb-0.5 font-medium">Mensagem desta execução</p>
        <pre class="mt-0 text-slate-100 whitespace-pre-wrap break-words font-sans text-[13px] leading-relaxed">{{ inputUserMessage() }}</pre>
      </div>
      <p v-if="runInputObject().conversation_id" class="text-slate-300">
        <strong class="text-slate-400">Sessão:</strong>
        <span class="ml-1 font-mono text-[11px] text-cyan-100/90 break-all">{{ runInputObject().conversation_id }}</span>
      </p>
      <p v-if="coordinatorDisplayLabel()" class="text-slate-300">
        <strong class="text-slate-400">Coordenador:</strong> {{ coordinatorDisplayLabel() }}
      </p>
      <div
        v-if="splitBatchFromInput()"
        class="mt-2 rounded-md border border-amber-300/35 bg-amber-950/25 p-2 space-y-1.5"
      >
        <p class="text-[11px] uppercase tracking-wide text-amber-200/90 font-semibold">Lote split (coordenador)</p>
        <p class="text-slate-200">
          <strong class="text-slate-400">Parte:</strong> {{ splitBatchFromInput().partIndex }} / {{ splitBatchFromInput().partsTotal }}
        </p>
        <p class="text-slate-200">
          <strong class="text-slate-400">Extração do contexto global:</strong>
          <span class="ml-1 font-mono text-[11px] text-amber-100/90">{{ splitBatchFromInput().source }}</span>
        </p>
        <details v-if="splitBatchFromInput().sharedText" class="rounded border border-slate-600/40 bg-slate-900/30 px-2 py-1">
          <summary class="cursor-pointer text-amber-200/90 hover:text-amber-100 text-[11px] select-none">Texto injetado em todos os subpedidos</summary>
          <pre class="mt-1 text-slate-200 whitespace-pre-wrap break-words text-[12px] leading-relaxed max-h-48 overflow-y-auto">{{ splitBatchFromInput().sharedText }}</pre>
        </details>
        <details class="rounded border border-slate-600/40 bg-slate-900/30 px-2 py-1">
          <summary class="cursor-pointer text-slate-400 hover:text-slate-300 text-[11px] select-none">Mensagem original do usuário (antes do split)</summary>
          <pre class="mt-1 text-slate-300 whitespace-pre-wrap break-words text-[12px] leading-relaxed max-h-40 overflow-y-auto">{{ splitBatchFromInput().originalMessage || '—' }}</pre>
        </details>
      </div>
      <details v-if="inputConversationHistory().length" class="rounded-md border border-slate-600/50 bg-slate-900/25 px-2 py-1">
        <summary class="cursor-pointer text-cyan-200/95 hover:text-cyan-100 select-none py-1">
          Histórico da conversa ({{ inputConversationHistory().length }} mensagens)
        </summary>
        <ul class="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1 border-t border-slate-600/40 pt-2">
          <li
            v-for="(turn, idx) in inputConversationHistory()"
            :key="`h-${idx}`"
            class="rounded border border-slate-600/40 bg-[#1e293b]/60 p-2"
          >
            <span
              class="inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded mb-1"
              :class="
                (turn.role || '').toString().toLowerCase() === 'user'
                  ? 'bg-sky-500/20 text-sky-200'
                  : 'bg-violet-500/20 text-violet-200'
              "
            >{{ historyRoleLabel(turn.role) }}</span>
            <pre class="text-slate-200 whitespace-pre-wrap break-words font-sans text-[12px] leading-snug mt-1">{{ turn.content }}</pre>
          </li>
        </ul>
      </details>
      <details v-if="Object.keys(inputOtherFields()).length" class="rounded-md border border-slate-600/50 bg-slate-900/25 px-2 py-1">
        <summary class="cursor-pointer text-cyan-200/95 hover:text-cyan-100 select-none py-1">
          Metadados / roteamento
        </summary>
        <pre class="mt-2 text-slate-300 whitespace-pre-wrap break-words text-[11px] leading-relaxed max-h-48 overflow-y-auto">{{ prettyJson(inputOtherFields()) }}</pre>
      </details>
      <details class="rounded-md border border-slate-600/40 bg-slate-950/30 px-2 py-1">
        <summary class="cursor-pointer text-slate-400 hover:text-slate-300 select-none py-1 text-[11px]">
          JSON bruto da entrada
        </summary>
        <pre class="mt-2 text-slate-400 whitespace-pre-wrap break-words text-[10px] leading-relaxed max-h-56 overflow-y-auto font-mono">{{ prettyJson(runDetail.input_payload) }}</pre>
      </details>
    </div>

    <div class="mb-4 rounded-lg border border-slate-600/60 bg-[#2f4563]/50 p-3 space-y-2 text-xs text-slate-300">
      <p class="text-[11px] uppercase tracking-wide text-cyan-200/90 font-semibold">Saída</p>
      <div v-if="hasStructuredOutput()" class="flex flex-wrap gap-2 items-center">
        <span
          v-if="runOutputObject().provider"
          class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-200 border border-emerald-400/25"
        >{{ runOutputObject().provider }}</span>
        <span
          v-if="runOutputObject().model"
          class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-500/15 text-indigo-200 border border-indigo-400/25"
        >{{ runOutputObject().model }}</span>
      </div>
      <details v-if="runOutputObject().text != null && String(runOutputObject().text).length" class="rounded-md border border-slate-600/50 bg-slate-900/25 px-2 py-1">
        <summary class="cursor-pointer text-cyan-200/95 hover:text-cyan-100 select-none py-1 font-medium">
          Resposta do assistente (vai ao chat)
        </summary>
        <pre class="mt-1 text-slate-100 whitespace-pre-wrap break-words font-sans text-[13px] leading-relaxed max-h-80 overflow-y-auto border border-slate-600/40 rounded-md p-2 bg-slate-950/35">{{ runOutputObject().text }}</pre>
      </details>
      <p v-else-if="!hasAnyOutputPayload()" class="text-slate-500 italic">Sem saída registrada ainda.</p>
      <p v-else class="text-slate-400">
        Não há <span class="font-mono text-slate-300">text</span> na saída; use <strong class="text-slate-300">Outros campos</strong> ou o JSON bruto.
      </p>
      <details v-if="Object.keys(outputExtraFields()).length" class="rounded-md border border-slate-600/50 bg-slate-900/25 px-2 py-1">
        <summary class="cursor-pointer text-cyan-200/95 hover:text-cyan-100 select-none py-1">
          Outros campos da saída
        </summary>
        <pre class="mt-2 text-slate-300 whitespace-pre-wrap break-words text-[11px] leading-relaxed max-h-48 overflow-y-auto">{{ prettyJson(outputExtraFields()) }}</pre>
      </details>
      <details v-if="hasAnyOutputPayload()" class="rounded-md border border-slate-600/40 bg-slate-950/30 px-2 py-1">
        <summary class="cursor-pointer text-slate-400 hover:text-slate-300 select-none py-1 text-[11px]">
          JSON bruto da saída
        </summary>
        <pre class="mt-2 text-slate-400 whitespace-pre-wrap break-words text-[10px] leading-relaxed max-h-56 overflow-y-auto font-mono">{{ prettyJson(runDetail.output_payload) }}</pre>
      </details>
    </div>
    <ol class="mt-4 space-y-2">
      <li
        v-for="s in runDetail.steps || []"
        :key="s.id"
        class="text-xs border border-slate-600/70 rounded-lg p-2 bg-[#2f4563]/70"
      >
        <span class="font-mono text-cyan-300">{{ s.sequence }}. {{ s.kind }}</span>
        <div
          v-if="s.kind === 'split_batch_context'"
          class="mt-2 rounded-md border border-amber-300/30 bg-slate-900/40 p-2 space-y-1.5 text-slate-200"
        >
          <p class="text-[11px] uppercase text-amber-200/90 font-semibold">Auditoria — contexto do split</p>
          <p class="text-xs">
            <strong>Parte:</strong> {{ splitBatchStepPayload(s).part_index }} / {{ splitBatchStepPayload(s).parts_total }}
            <span class="text-slate-500 mx-1">·</span>
            <strong>Fonte:</strong> {{ splitBatchStepPayload(s).shared_constraints_source || '—' }}
          </p>
          <details v-if="splitBatchStepPayload(s).shared_constraints_text" class="text-xs">
            <summary class="cursor-pointer text-amber-200/90 hover:text-amber-100">Contexto compartilhado (preview no log)</summary>
            <pre class="mt-1 text-slate-300 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{{ splitBatchStepPayload(s).shared_constraints_text }}</pre>
          </details>
          <details v-if="splitBatchStepPayload(s).original_user_message_preview" class="text-xs">
            <summary class="cursor-pointer text-slate-400 hover:text-slate-300">Mensagem original (preview)</summary>
            <pre class="mt-1 text-slate-400 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">{{ splitBatchStepPayload(s).original_user_message_preview }}</pre>
          </details>
        </div>
        <div
          v-if="isApiToolResultStep(s)"
          class="mt-2 rounded-md border border-cyan-300/30 bg-slate-900/40 p-2 space-y-2"
        >
          <p class="text-[11px] uppercase tracking-wide text-cyan-200/90 font-semibold">Chamada da API</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-1 text-slate-200">
            <p><strong>Nome:</strong> {{ apiToolResultPayload(s).name || '—' }}</p>
            <p><strong>Operation key:</strong> {{ apiToolResultPayload(s).operation_key || '—' }}</p>
            <p><strong>Endpoint ID:</strong> {{ apiToolResultPayload(s).endpoint_id ?? '—' }}</p>
            <p><strong>HTTP status:</strong> {{ apiToolResultPayload(s).http_status ?? '—' }}</p>
          </div>
          <p class="text-slate-200 break-all">
            <strong>URL:</strong> {{ apiToolResultPayload(s).request_url || '—' }}
          </p>
          <div>
            <p class="text-slate-200 mb-1"><strong>Parâmetros enviados (request_payload):</strong></p>
            <pre class="text-slate-300 whitespace-pre-wrap break-words">{{ prettyJson(apiToolResultPayload(s).request_payload) }}</pre>
          </div>
          <details v-if="apiToolResultPayload(s).response_payload != null" class="group">
            <summary class="cursor-pointer text-cyan-200 hover:text-cyan-100">
              Ver response_payload
            </summary>
            <pre class="mt-1 text-slate-300 whitespace-pre-wrap break-words">{{ prettyJson(apiToolResultPayload(s).response_payload) }}</pre>
          </details>
        </div>
        <details v-if="s.kind === 'tool_result'" class="mt-2 rounded-md border border-slate-600/45 bg-slate-950/30 px-2 py-1">
          <summary class="cursor-pointer text-slate-300 hover:text-slate-200 select-none text-[11px]">
            Ver JSON completo do tool_result
          </summary>
          <pre class="mt-2 text-slate-300 whitespace-pre-wrap break-words max-h-72 overflow-y-auto">{{ jsonPreview(s.payload) }}</pre>
        </details>
        <pre v-else class="mt-1 text-slate-300 whitespace-pre-wrap break-words">{{ jsonPreview(s.payload) }}</pre>
      </li>
    </ol>
  </div>
</template>

<script>
import { useAgentUi } from '../composables/useAgentUi'

export default {
  props: {
    runDetail: {
      type: Object,
      default: null
    }
  },

  setup() {
    const { jsonPreview } = useAgentUi()
    return { jsonPreview }
  },

  methods: {
    stepPayload(step) {
      const payload = step?.payload
      if (payload && typeof payload === 'object') return payload
      if (typeof payload !== 'string') return {}
      try {
        const parsed = JSON.parse(payload)
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch (_) {
        return {}
      }
    },

    isApiToolResultStep(step) {
      if (!step || step.kind !== 'tool_result') return false
      const payload = this.stepPayload(step)
      return payload.source === 'api_endpoint'
    },

    apiToolResultPayload(step) {
      return this.stepPayload(step)
    },

    prettyJson(value) {
      if (value == null) return '—'
      if (typeof value === 'string') return value
      try {
        return JSON.stringify(value, null, 2)
      } catch (_) {
        return String(value)
      }
    },

    parsedRunPayload(raw) {
      if (raw == null) return {}
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) return { ...raw }
      if (typeof raw === 'string') {
        try {
          const p = JSON.parse(raw)
          return p && typeof p === 'object' && !Array.isArray(p) ? p : {}
        } catch (_) {
          return {}
        }
      }
      return {}
    },

    runInputObject() {
      return this.parsedRunPayload(this.runDetail?.input_payload)
    },

    runOutputObject() {
      return this.parsedRunPayload(this.runDetail?.output_payload)
    },

    inputUserMessage() {
      const o = this.runInputObject()
      const t = (o.message || o.query || '').toString().trim()
      return t.length ? t : null
    },

    inputConversationHistory() {
      const h = this.runInputObject().conversation_history
      return Array.isArray(h) ? h : []
    },

    inputOtherFields() {
      const o = this.runInputObject()
      const skip = new Set([ 'message', 'query', 'conversation_id', 'conversation_history', 'split_batch' ])
      return Object.keys(o).reduce((acc, k) => {
        if (!skip.has(k)) acc[k] = o[k]
        return acc
      }, {})
    },

    splitBatchFromInput() {
      const raw = this.runInputObject().split_batch
      if (!raw || typeof raw !== 'object') return null
      const partIndex = raw.part_index ?? raw['part_index']
      const partsTotal = raw.parts_total ?? raw['parts_total']
      if (partIndex == null || partsTotal == null) return null
      return {
        partIndex: Number(partIndex),
        partsTotal: Number(partsTotal),
        source: (raw.shared_constraints_source ?? raw['shared_constraints_source'] ?? '—').toString(),
        sharedText: (raw.shared_constraints_text ?? raw['shared_constraints_text'] ?? '').toString(),
        originalMessage: (raw.original_user_message ?? raw['original_user_message'] ?? '').toString()
      }
    },

    splitBatchStepPayload(step) {
      return this.stepPayload(step)
    },

    outputExtraFields() {
      const o = this.runOutputObject()
      const skip = new Set([ 'text', 'model', 'provider' ])
      return Object.keys(o).reduce((acc, k) => {
        if (!skip.has(k)) acc[k] = o[k]
        return acc
      }, {})
    },

    historyRoleLabel(role) {
      const r = (role || '').toString().toLowerCase()
      if (r === 'user') return 'Usuário'
      if (r === 'assistant') return 'Assistente'
      return (role || '—').toString()
    },

    coordinatorDisplayLabel() {
      const cid = Number(this.runDetail?.coordinator_agent_id)
      if (!Number.isFinite(cid) || cid <= 0) return null
      const n = this.runDetail?.coordinator_name
      if (n) return `${n} (id ${cid})`
      return `id ${cid}`
    },

    hasStructuredOutput() {
      const o = this.runOutputObject()
      return !!(o.provider || o.model)
    },

    hasAnyOutputPayload() {
      const raw = this.runDetail?.output_payload
      if (raw == null) return false
      if (typeof raw === 'object' && !Array.isArray(raw)) return Object.keys(raw).length > 0
      if (typeof raw === 'string') return raw.trim().length > 0
      return true
    }
  }
}
</script>
